/**
 * Where an unsent note waits in this browser (2026-10-05).
 *
 * A note draft and a note whose send has not been confirmed are kept so a
 * failed send, a closed panel or a reload never loses a PA's words. They are
 * resident notes, so they are PHI, on a machine the office shares. The rules:
 *
 *   - Keyed by the SIGNED-IN USER and the resident, with the day it was saved
 *     IN THE KEY NAME: `pa.notes.v3:<kind>:<userId>:<patientId>:<epochDay>`.
 *     Only the signed-in user's own keys are ever read.
 *   - Another person's key is never read and never deleted while it is live
 *     (fix round 3): her session may have expired with a note unsent, and she
 *     comes back to it. The day in the name lets an EXPIRED key be removed
 *     without reading it; that is the only way this browser touches it.
 *   - No user, no storage: without a signed-in user id the words live in the
 *     field only.
 *   - Minimal: a draft is its text (plus, while it is an unsent note being
 *     edited, which note: its key, case, kind and original words); an outbox
 *     entry is the idempotency key, the case, the kind and the text, and why
 *     it waits when it is not simply unsent ('kept' / 'editing', fix round 4).
 *     No author, no resident name, no error. Each value carries the time it
 *     was saved.
 *   - Nothing typed is ever dropped but by the PA's own Discard (fix round 4):
 *     Edit keeps the edited note here as 'editing' and sets any other words in
 *     the box aside as 'kept'.
 *   - A note the server has confirmed is removed at once, by its key. Every
 *     unsent note has its OWN storage key (fix round 6), so a write touches one
 *     note and never reads-modifies-writes a shared list: two tabs on the same
 *     resident cannot drop each other's note or put back one the other confirmed.
 *   - Older than 7 days: dropped on read, and removed unread by the prune.
 *   - A sign-out clears the signed-in user's own keys only (clearUserScopedStorage),
 *     after asking when any of them still holds words (unsentNoteResidents).
 *   - Every storage access is in try/catch: a private window, blocked site
 *     data or a full quota degrade to "the words live in the field only".
 *
 * The v1 and v2 keys (2026-10-05, never released) carry no day, so their age
 * cannot be told without reading them; they are removed unread.
 *
 * ONE BOX PER TAB (fix round 5). The box's words are kept under the tab that
 * typed them: `pa.notes.v3:draft:<userId>:<patientId>:<tabId>:<epochDay>`, the
 * tab id living in sessionStorage (a reload keeps it, a new tab gets its own).
 * Two tabs on one resident used to share one draft key, so a Send or a
 * keystroke in one silently emptied or overwrote the other's words. Now:
 *   - a tab writes and clears only its own draft key;
 *   - while a thread is open, its tab says so (`pa.tabs.v1:<tabId>:<hash>:<day>`,
 *     a beat every 30 s, removed when the thread closes or the page goes;
 *     the hash names the user and resident without naming them);
 *   - when a thread opens, another tab's draft whose thread is NOT open any
 *     more (the tab was closed, the panel moved on, the old shared key) is
 *     moved into the outbox as a "Draft kept" item, never dropped;
 *   - a duplicated tab (same tab id) that writes this tab's key is noticed
 *     (a storage event, or the value is not what this tab last wrote): this
 *     tab takes a new id and keeps its words under it, leaving the other's.
 *
 * FIX ROUND 8: a "sent" mark (`pa.sent.v1:`, no words) for an adopted copy that was recorded, so the tab
 * the words came from never writes them back; "thread open" marks pruned by their own time, not by a UTC
 * day; and a sign-out marker (config/authRoutes) that stops an open tab writing back what it deleted.
 *
 * FIX ROUND 9: a "sent" mark speaks only for the exact box it names (no fallback), and an opening thread
 * clears its tab's marks; every write lands (and is read back) before anything it replaces is removed --
 * a new day's key, an adopted box's copy -- so a full quota never leaves words nowhere; and the box keeps
 * its caution (a time, no words) so a reload keeps the duplicate check.
 *
 * FIX ROUND 10: the caution travels with the words -- an unsent note carries it too (set aside by Edit, kept
 * from a closed tab's box), so its Send asks first as the box's does; and no value is ever deleted for being
 * dated in the future (the clock was moved back): a future time counts as now, and only age expires it.
 *
 * FIX ROUND 11: a future time is RE-DATED, not merely forgiven. The first read of a value saved "in the
 * future" saves it again dated now, and the prune renames a key whose name is dated after today to today
 * (copying its bytes, unread), so it expires seven days from now instead of never. The sign-out count is
 * age-only too (config/authRoutes), and a future sign-out marker is re-dated to just before now. A caution
 * also carries `partial` -- part of the words went from another tab -- wherever the words go.
 *
 * WHAT REMAINS BY DESIGN (the last planned round, 2026-10-06):
 *   - The duplicate check behind a caution reads the thread back at most REPLAY_PAGES pages of 50 (to a day
 *     before the caution); past that, or when the thread cannot be read, it says so and asks. It compares her
 *     own PA notes only, by words: a copy that was edited on the way is not recognised.
 *   - Clock skew is handled by AGE alone: a future time counts as now and is re-dated to now on its first
 *     read (or renamed by the prune), so a value saved under a clock running ahead lives up to 7 days from
 *     when this computer next sees it, not from when it was written. Nothing is deleted for being in the future.
 *   - Storage full or blocked: words that cannot be written stay in memory, on screen, marked "Not saved on
 *     this computer", and leaving or reloading the page warns first (beforeunload). Fix round 12: they also
 *     stay in this page's memory (heldInMemory, below) while the thread is not on screen -- another section of
 *     the panel, Prev/Next, the panel closed -- and come back when it opens again; the warning stays up meanwhile.
 *     A reload or a crash past that warning regardless loses them; no second store (sessionStorage) is used, so
 *     the sign-out rules stay one place. A sign-out drops them from memory too, without asking first: the
 *     sign-out question counts the words kept in storage only.
 *   - A renamed key of ANOTHER person (the prune) is copied as bytes and never parsed; under a full quota the
 *     copy can fail, and that key then keeps its future name until there is room.
 *   - "Sent" marks and presence marks hold no words; they are re-dated or pruned like the rest, best effort.
 *   - A sign-out under a full quota writes its marker after her keys are removed (fix round 14); if storage is
 *     full of OTHER data even then, there is no marker, and only the workspace leaving with no session of hers
 *     (forgetMemoryWithoutSession) drops words this page held in memory.
 *   - Re-dating a value read "in the future" re-reads it just before writing and writes only if it is unchanged
 *     (fix round 12); localStorage has no compare-and-set, so a write by another tab in the instant between that
 *     re-read and the write can still be overwritten with the value as read.
 */
import { SIGNED_OUT_PREFIX, USER_SCOPED_STORAGE_PREFIXES, USER_SCOPED_TTL_MS, sessionUserId, signedOutAt, signedOutKey } from '../../config/authRoutes';

/** Registered with the sign-out cleanup (config/authRoutes), so a sign-out clears it. */
export const NOTE_STORAGE_PREFIX: string = USER_SCOPED_STORAGE_PREFIXES[0];
const V3 = `${NOTE_STORAGE_PREFIX}v3:`;
export const NOTE_STORAGE_TTL_MS = USER_SCOPED_TTL_MS;
const DAY_MS = 24 * 60 * 60 * 1000;

export type StoredKind = 'GENERAL' | 'VETTING';
/**
 * One unsent note. `hold` says why it waits (fix round 4): absent, it was sent and not confirmed;
 * 'kept', it is a draft the PA set aside (never sent) when Edit brought another note into the box;
 * 'editing', its words are in the composer right now -- it stays here, with its ORIGINAL key, until
 * it is sent or discarded, so a reload never loses it and a resend never records it twice. `from`
 * is what an editing note goes back to when the editing stops (absent: an unsent note).
 */
export type StoredHold = 'kept' | 'editing';
/**
 * `why` (fix round 5) says why a kept draft is kept when it is not the round-4 "set aside by Edit":
 * 'tab', it was left in the box of a tab that closed; 'earlier', its key had already recorded an
 * earlier version (other words, another kind or another case), so this version was not recorded.
 */
export type StoredWhy = 'tab' | 'earlier';
/**
 * `caution` (fix round 10): these words were in a box written back under a caution (another tab MAY have sent
 * them) when they moved into the list -- set aside by Edit, or kept from a closed tab's box. A time, no words,
 * as on the box: Send on this note first looks for the same words in the thread, exactly as the box's Send does.
 * `partial` (fix round 11, only with a caution): PART of these words went from another tab (the box was taken
 * and sent as it was saved, and more was typed since); kept wherever the caution goes, so the note says so.
 */
export interface StoredNote { key: string; caseId: string; kind: StoredKind; note: string; hold?: StoredHold; from?: 'kept'; why?: StoredWhy; caution?: number; partial?: boolean }
type What = 'draft' | 'outbox';

const dayOf = (t: number) => Math.floor(t / DAY_MS);
/**
 * How old a value saved at `t` is (fix round 10). A time in the future -- this computer's clock was moved
 * back after the value was saved, or another tab's clock runs ahead -- counts as saved NOW: it is never
 * deleted for being in the future, only for its age, so moving the clock back never removes words unread.
 * Fix round 11: "now" is made true -- the first read of such a value saves it again dated now (readEntry), and
 * the prune renames a future-named key to today -- so it ages from then. It used to count as "now" forever and
 * never expire: PHI kept past the 7 days.
 */
const ageOf = (t: number, now: number) => Math.max(0, now - t);
const fresh = (savedAt: unknown, now: number): savedAt is number => typeof savedAt === 'number' && Number.isFinite(savedAt) && ageOf(savedAt, now) <= NOTE_STORAGE_TTL_MS;
/** Every key of one user's value for one resident starts with this; the day follows. */
export const noteKeyStem = (what: What, userId: string, patientId: string) => `${V3}${what}:${userId}:${patientId}:`;
/** This tab's own draft keys for one resident: the resident's draft stem, then the tab id. */
export const draftTabStem = (userId: string, patientId: string, tab: string) => `${noteKeyStem('draft', userId, patientId)}${tab}:`;
const usable = (userId: string | null | undefined, patientId: string | null | undefined): userId is string =>
  Boolean(userId && patientId);

function store(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}

/* ── This tab ─────────────────────────────────────────────────────────────── */

const TAB_KEY = 'pa.tab.v1';
const TAB_RE = /^t[a-z0-9]{6,24}$/;
let tabCache: string | null = null;
const newTabId = () => `t${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
/** This tab's id: kept in sessionStorage, so a reload keeps it and another tab has its own. Never all digits. */
export function currentTabId(): string {
  if (tabCache) return tabCache;
  let id: string | null = null;
  try { id = window.sessionStorage.getItem(TAB_KEY); } catch { id = null; }
  if (!id || !TAB_RE.test(id)) {
    id = newTabId();
    try { window.sessionStorage.setItem(TAB_KEY, id); } catch { /* this page load only */ }
  }
  tabCache = id;
  return id;
}
/** A new id for this tab: another tab (a duplicate) holds the same one. */
export function renewTabId(): string {
  const id = newTabId();
  try { window.sessionStorage.setItem(TAB_KEY, id); } catch { /* this page load only */ }
  tabCache = id;
  return id;
}

/* ── Which tabs have a thread open (no names: a hash of user and resident) ── */

export const PRESENCE_PREFIX = 'pa.tabs.v1:';
/** A tab that has not beaten for this long has no thread open (a hidden tab beats at least once a minute). */
export const PRESENCE_STALE_MS = 3 * 60 * 1000;
/**
 * A "thread open" mark is pruned only when its own time is this old (fix round 8). A visible tab beats every
 * 30 s and a hidden one at least once a minute, so a box is never more than about a minute newer than its
 * tab's last beat: past an hour, the box is old too, and the quiet-tab rule would take it anyway.
 */
export const PRESENCE_PRUNE_MS = 60 * 60 * 1000;
function hashOf(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) { h ^= text.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(36);
}
const presenceStem = (tab: string, userId: string, patientId: string) => `${PRESENCE_PREFIX}${tab}:${hashOf(`${userId}:${patientId}`)}:`;
/** True when a storage event is a tab closing (or opening) a thread on this user's resident. */
export function isPresenceKeyFor(storageKey: string | null, userId: string | null | undefined, patientId: string | null | undefined): boolean {
  if (!storageKey || !usable(userId, patientId)) return false;
  const h = `:${hashOf(`${userId}:${patientId}`)}:`;
  return storageKey.startsWith(PRESENCE_PREFIX) && storageKey.includes(h);
}
/** This tab has the thread open (written on open and every 30 s). */
export function markThreadOpen(userId: string | null | undefined, patientId: string | null | undefined, tab: string, now = Date.now()): void {
  if (!usable(userId, patientId)) return;
  writeOwn(presenceStem(tab, userId, patientId!), { at: now }, now);
}
/** This tab closed the thread (or the page is going away). */
export function markThreadClosed(userId: string | null | undefined, patientId: string | null | undefined, tab: string): void {
  if (!usable(userId, patientId)) return;
  const s = store();
  if (!s) return;
  try { keysStartingWith(s, presenceStem(tab, userId, patientId!)).forEach((k) => { try { s.removeItem(k); } catch { /* keep going */ } }); } catch { /* storage unavailable */ }
}
/** Has that tab beaten on this resident's thread within the last 3 minutes? */
export function isThreadOpenIn(tab: string, userId: string, patientId: string, now = Date.now()): boolean {
  return threadPresence(tab, userId, patientId, now) === 'open';
}
/**
 * What that tab last said about this resident's thread (fix round 7): 'open', beating; 'closed', it said
 * so (or never had it open); 'quiet', it said it was open and then stopped beating -- closed without a
 * word (a crash), or frozen or asleep (a background tab the browser froze, a laptop lid) with its words
 * still in memory.
 */
export function threadPresence(tab: string, userId: string, patientId: string, now = Date.now()): 'open' | 'quiet' | 'closed' {
  const v = readOwn(presenceStem(tab, userId, patientId), now) as { at?: unknown } | null;
  if (!v || typeof v.at !== 'number') return 'closed';
  return now - v.at <= PRESENCE_STALE_MS ? 'open' : 'quiet';
}

function keysStartingWith(s: Storage, stem: string): string[] {
  const out: string[] = [];
  for (let i = 0; i < s.length; i += 1) {
    const k = s.key(i);
    if (k && k.startsWith(stem)) out.push(k);
  }
  return out;
}

/** The day a v3 key was saved, from its NAME alone; null for anything else. */
function keyDay(k: string): number | null {
  if (!k.startsWith(V3)) return null;
  const m = /:(\d{1,7})$/.exec(k);
  return m ? Number(m[1]) : null;
}
/**
 * Past 7 days by its name (a day of slack, since the name holds only the day; the value's own time is exact).
 * Fix round 10: a name dated AFTER today is not expired -- the clock was moved back. Fix round 11: the prune
 * renames it to today, so it then ages from today by its name like any other key.
 */
const expiredByName = (day: number, now: number) => day < dayOf(now - NOTE_STORAGE_TTL_MS);

/** Read one of the signed-in user's own values, dropping it if expired or malformed. */
function readOwn(stem: string, now: number): unknown {
  const e = readEntry(stem, now);
  return e ? e.value : null;
}

/**
 * Write one of the signed-in user's values. `now` is when its WORDS were written: the 7-day limit and the
 * day in the key's name count from it. `order` (fix round 7) is a separate list position, kept when a
 * note's words change, so an edited note keeps its place in the list but never its old note's expiry.
 *
 * Fix round 9: the new value is written FIRST and read back, and only then do the keys of other days go.
 * Under a full quota a write after UTC midnight (a new key name) used to remove the old day's key and then
 * fail, leaving the words nowhere. Now a write that does not land leaves the old value where it was.
 * True when the value (or its removal) is really in storage.
 */
function writeOwn(stem: string, value: unknown | null, now: number, order?: number): boolean {
  const s = store();
  if (!s) return false;
  try {
    const next = `${stem}${dayOf(now)}`;
    const others = keysStartingWith(s, stem).filter((k) => k !== next);
    if (value === null) {
      s.removeItem(next);
    } else {
      const body = JSON.stringify(order !== undefined && order !== now ? { savedAt: now, order, value } : { savedAt: now, value });
      s.setItem(next, body);
      if (s.getItem(next) !== body) return false;
    }
    others.forEach((k) => { try { s.removeItem(k); } catch { /* keep going */ } });
    return true;
  } catch { return false; /* quota or blocked: what was stored stays; the words are on screen */ }
}

/**
 * Remove, WITHOUT reading them, the note keys whose name says they are past
 * 7 days (anyone's), and the dayless v1/v2 keys. A live key of another person
 * is left alone: it is unreadable here, and it is hers. Runs when a thread opens.
 */
export function pruneExpiredNoteKeys(now = Date.now()): void {
  const s = store();
  if (!s) return;
  try {
    const doomed: string[] = [];
    // Fix round 11: a name dated after today (saved while the clock ran ahead) is renamed to today -- its
    // bytes copied, never parsed, so another person's key stays unread -- and from then on it expires by its
    // name like any other. Copied first; the old name goes only once the copy is there. No copy, no change.
    const future: string[] = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (!k || !k.startsWith(NOTE_STORAGE_PREFIX)) continue;
      const day = keyDay(k);
      if (day === null || expiredByName(day, now)) doomed.push(k);
      else if (day > dayOf(now)) future.push(k);
    }
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k && k.startsWith(SENT_PREFIX) && /:(\d{1,7})$/.test(k) && Number(/:(\d{1,7})$/.exec(k)![1]) > dayOf(now)) future.push(k);
    }
    future.forEach((k) => renameToToday(s, k, now));
    // A tab's "thread open" mark (fix round 8). Its name's day is a UTC day, so "an earlier day" is NOT
    // "stale": a tab that beat ten seconds before UTC midnight is open twenty seconds after it, and used to
    // be pruned here and read as closed, its box taken at once. Now a mark goes when its day is earlier
    // than yesterday, or when its own time (a timestamp, no name) is long past -- past PRESENCE_PRUNE_MS,
    // well beyond PRESENCE_STALE_MS, so a QUIET tab (frozen, asleep) still reads as quiet and keeps the
    // round-7 rule that its box is taken only once the box's own words are old too.
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (!k || !k.startsWith(PRESENCE_PREFIX)) continue;
      const m = /:(\d{1,7})$/.exec(k);
      if (!m || Number(m[1]) < dayOf(now) - 1) { doomed.push(k); continue; }
      let at: unknown = null;
      try { const v = JSON.parse(s.getItem(k) || 'null'); at = v && v.value ? v.value.at : null; } catch { at = null; }
      if (typeof at !== 'number' || now - at > PRESENCE_PRUNE_MS) { doomed.push(k); continue; }
      // Fix round 11: a beat dated in the future is a beat now (so it goes stale, and is pruned, from now).
      if (at > now) { try { s.setItem(k, JSON.stringify({ savedAt: now, value: { at: now } })); } catch { /* left as it was */ } }
    }
    // "Sent from another tab" marks (fix round 8): text-free; gone by the day in their name, like a note.
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (!k || !k.startsWith(SENT_PREFIX)) continue;
      const m = /:(\d{1,7})$/.exec(k);
      if (!m || expiredByName(Number(m[1]), now)) doomed.push(k);
    }
    // A sign-out marker (a time, no name) older than the notes it guards against.
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (!k || !k.startsWith(SIGNED_OUT_PREFIX)) continue;
      const at = Number(s.getItem(k));
      if (!Number.isFinite(at) || now - at > NOTE_STORAGE_TTL_MS) { doomed.push(k); continue; }
      // Fix round 11: a marker dated in the future (a sign-out while the clock ran ahead) happened before now:
      // re-dated to just before now, so it expires, and a page opened from now on is not taken for an older one.
      if (at > now) { try { s.setItem(k, String(now - 1)); } catch { /* config/authRoutes re-dates it on read */ } }
    }
    doomed.forEach((k) => { try { s.removeItem(k); } catch { /* keep going */ } });
  } catch { /* storage unavailable */ }
}

/** Rename one key whose name is dated after today to today's name (fix round 11): copied as bytes, unread. */
function renameToToday(s: Storage, k: string, now: number): void {
  try {
    const m = /:(\d{1,7})$/.exec(k);
    if (!m) return;
    const next = `${k.slice(0, m.index)}:${dayOf(now)}`;
    const body = s.getItem(k);
    if (body === null) return;
    // A key already under today's name is never overwritten: both stay, and the owner's next read settles
    // them (writeOwn removes a stem's other days once a write lands, so two live at once only after a failed removal).
    if (s.getItem(next) !== null) return;
    s.setItem(next, body);
    if (s.getItem(next) !== body) return;
    s.removeItem(k);
  } catch { /* quota or blocked: the old name stays until there is room */ }
}

/**
 * The composer's words. A plain draft is its text. A draft that is an unsent note being edited also
 * carries WHICH note (`edit`: its idempotency key and its original case, kind and words), so Edit, a
 * reload and Send are one note, never two: sent unchanged it goes with the original key, and the
 * server answers a note that did land with that one instead of recording it again.
 */
export interface DraftEdit { key: string; caseId: string; kind: StoredKind; note: string }
/**
 * `caution` (fix round 9): the box was written back by a waking tab that could not tell whether another
 * tab sent its words. A time, no words: the earliest a copy of these words could have been recorded (when
 * the box was last saved before it was taken). Kept with the box so a reload keeps the caution and its
 * duplicate check; absent, no caution.
 */
export interface StoredDraft { text: string; edit: DraftEdit | null; caution?: number; partial?: boolean }
/**
 * Where the box was pointed when its words were saved: the case and kind a leftover draft is kept under (and its
 * caution; `partial`, fix round 11: part of the words went from another tab -- kept only with a caution).
 */
export interface DraftAim { caseId?: string | null; kind?: StoredKind | null; caution?: number; partial?: boolean }
function asEdit(v: unknown): DraftEdit | null {
  const e = v as Partial<DraftEdit> | null;
  if (!e || typeof e !== 'object' || typeof e.key !== 'string' || !e.key || typeof e.caseId !== 'string' || typeof e.note !== 'string') return null;
  return { key: e.key, caseId: e.caseId, kind: e.kind === 'VETTING' ? 'VETTING' : 'GENERAL', note: e.note };
}
function asDraft(v: unknown): StoredDraft & DraftAim {
  if (typeof v === 'string') return { text: v, edit: null };
  if (v && typeof v === 'object' && typeof (v as { text?: unknown }).text === 'string') {
    const o = v as { text: string; edit?: unknown; caseId?: unknown; kind?: unknown; caution?: unknown; partial?: unknown };
    const cautioned = typeof o.caution === 'number' && Number.isFinite(o.caution) && o.caution > 0;
    return {
      text: o.text, edit: asEdit(o.edit),
      caseId: typeof o.caseId === 'string' ? o.caseId : null,
      kind: o.kind === 'VETTING' ? 'VETTING' : o.kind === 'GENERAL' ? 'GENERAL' : null,
      ...(cautioned ? { caution: o.caution as number, ...(o.partial === true ? { partial: true } : {}) } : {}),
    };
  }
  return { text: '', edit: null };
}
/** This tab's box (`tab` defaults to this tab's id). Another tab's box is never read as this one's. */
export function readComposer(userId: string | null | undefined, patientId: string | null | undefined, now = Date.now(), tab: string = currentTabId()): StoredDraft {
  if (!usable(userId, patientId)) return { text: '', edit: null };
  const d = asDraft(readOwn(draftTabStem(userId, patientId!, tab), now));
  return { text: d.text, edit: d.edit, ...(d.caution ? { caution: d.caution, ...(d.partial ? { partial: true } : {}) } : {}) };
}

/**
 * Write (or, with no words, clear) THIS tab's box only. Another tab's box is never written or cleared here.
 * True when storage now holds exactly this (fix round 9: a failed write is not taken for a saved box).
 */
export function writeComposer(
  userId: string | null | undefined, patientId: string | null | undefined, text: string, edit: DraftEdit | null,
  now = Date.now(), tab: string = currentTabId(), aim: DraftAim = {},
): boolean {
  if (!usable(userId, patientId)) return false;
  const where = {
    ...(aim.caseId ? { caseId: aim.caseId } : {}), ...(aim.kind ? { kind: aim.kind } : {}),
    ...(aim.caution && aim.caution > 0 ? { caution: aim.caution, ...(aim.partial ? { partial: true } : {}) } : {}),
  };
  const value = edit
    ? { text, edit: { key: edit.key, caseId: edit.caseId, kind: edit.kind, note: edit.note }, ...where }
    : Object.keys(where).length ? { text, ...where } : text;
  return writeOwn(draftTabStem(userId, patientId!, tab), text ? value : null, now);
}

/** When this tab's box was last saved (0: no box). A waking tab names its box by this time (fix round 8). */
export function composerSavedAt(userId: string | null | undefined, patientId: string | null | undefined, now = Date.now(), tab: string = currentTabId()): number {
  if (!usable(userId, patientId)) return 0;
  const e = readEntry(draftTabStem(userId, patientId!, tab), now);
  return e ? e.savedAt : 0;
}

export function readDraft(userId: string | null | undefined, patientId: string | null | undefined, now = Date.now(), tab: string = currentTabId()): string {
  return readComposer(userId, patientId, now, tab).text;
}

export function writeDraft(userId: string | null | undefined, patientId: string | null | undefined, text: string, now = Date.now(), tab: string = currentTabId()): void {
  writeComposer(userId, patientId, text, null, now, tab);
}

/** Another tab's box for this resident, as stored. `tab` null: the old shared key (round 4 and before). */
export interface OtherDraft extends StoredDraft, DraftAim { storageKey: string; tab: string | null; savedAt: number }
/** Every OTHER tab's box this user has for this resident (never another user's). */
export function otherTabDrafts(userId: string | null | undefined, patientId: string | null | undefined, myTab: string, now = Date.now()): OtherDraft[] {
  if (!usable(userId, patientId)) return [];
  const s = store();
  if (!s) return [];
  const stem = noteKeyStem('draft', userId!, patientId!);
  const out: OtherDraft[] = [];
  try {
    for (const k of keysStartingWith(s, stem)) {
      const rest = k.slice(stem.length);
      const m = /^(?:(t[a-z0-9]{6,24}):)?(\d{1,7})$/.exec(rest);
      if (!m) continue;
      const tab = m[1] || null;
      if (tab === myTab) continue;
      let v: { savedAt?: unknown; value?: unknown } | null = null;
      try { v = JSON.parse(s.getItem(k) || 'null'); } catch { v = null; }
      const ok = v && typeof v === 'object' && fresh(v.savedAt, now);
      if (!ok) { try { s.removeItem(k); } catch { /* keep going */ } continue; }
      const d = asDraft(v!.value);
      if (!d.text) { try { s.removeItem(k); } catch { /* keep going */ } continue; }
      out.push({ ...d, storageKey: k, tab, savedAt: v!.savedAt as number });
    }
  } catch { /* storage unavailable */ }
  return out;
}

/**
 * A box left behind by a tab whose thread is not open any more (closed tab, panel moved on, the old
 * shared key) is moved into the outbox as a "Draft kept" item (why 'tab') and its key removed --
 * added FIRST, so there is no moment the words are kept nowhere. Words that are exactly the note the
 * box was editing are already in the outbox, so nothing is added for them. A box whose tab still has
 * the thread open is that tab's, and is left alone. The kept item's key is derived from the box
 * (tab and save time), so two tabs adopting the same box at once keep one copy. Returns how many.
 *
 * Fix round 7: a tab that went QUIET (it said it was open, then stopped beating) may only be frozen or
 * asleep, still holding its words. Its box is taken only when the box's own words are ALSO older than
 * the presence window -- words typed in the last 3 minutes belong to a tab that was just working. A tab
 * that does wake to find its box taken settles it (adoptedCopiesOf: the same words, the copy goes;
 * other words, it says both are kept).
 */
export function adoptLeftoverDrafts(
  userId: string | null | undefined, patientId: string | null | undefined, myTab: string, fallback: { caseId: string; kind?: StoredKind },
  now = Date.now(),
): number {
  if (!usable(userId, patientId)) return 0;
  let n = 0;
  for (const d of otherTabDrafts(userId, patientId, myTab, now)) {
    if (d.tab) {
      const presence = threadPresence(d.tab, userId!, patientId!, now);
      if (presence === 'open') continue;
      if (presence === 'quiet' && now - d.savedAt <= PRESENCE_STALE_MS) continue;
    }
    const words = d.text.trim();
    // Fix round 10: words that are exactly the note the box was editing are already in the list as that note;
    // a caution on the box goes onto it, so its Send checks the thread first.
    if (words && d.caution && d.edit && d.edit.note.trim() === words) setOutboxCaution(userId, patientId, d.edit.key, d.caution, now, d.partial);
    if (words && !(d.edit && d.edit.note.trim() === words)) {
      const key = adoptedKey(d.tab, userId!, patientId!, d.savedAt);
      addToOutbox(userId, patientId, {
        key,
        caseId: d.edit?.caseId || d.caseId || fallback.caseId,
        kind: d.edit?.kind || d.kind || fallback.kind || 'GENERAL',
        note: words, hold: 'kept', why: 'tab',
        // Fix round 10: the box's caution comes with its words. Without it, Send on this copy recorded
        // words another tab may already have sent, a second time, without asking.
        // Fix round 11: and whether PART of them went from another tab already (a 'partial' box).
        ...(d.caution ? { caution: d.caution, ...(d.partial ? { partial: true } : {}) } : {}),
      }, now);
      // Fix round 9: the box goes only once its copy is READ BACK from storage. Under a full quota the copy
      // could not be written and the box used to be removed anyway, its words kept nowhere. Now the box
      // stays where it is: its own tab, or a later open with room to keep the copy, takes it.
      const copy = readOutboxNote(userId, patientId, key, now);
      if (!copy || copy.note !== words || (d.caution && !copy.caution) || (d.partial && !copy.partial)) continue;
      n += 1;
    }
    const s = store();
    try { s?.removeItem(d.storageKey); } catch { /* keep going */ }
  }
  return n;
}

/** The key a box adopted from `tab` is kept under: derived from the box, so two adopters keep one copy. */
const adoptedKey = (tab: string | null, userId: string, patientId: string, savedAt: number) =>
  `kept-${tab || 'shared'}-${hashOf(`${userId}:${patientId}`)}-${savedAt.toString(36)}`;

/**
 * The "Draft kept" copies another tab made of THIS tab's box while it was quiet (frozen, asleep, in the
 * background) -- what a waking tab checks before it writes its box again (fix round 7).
 */
export function adoptedCopiesOf(userId: string | null | undefined, patientId: string | null | undefined, tab: string, now = Date.now()): StoredNote[] {
  if (!usable(userId, patientId)) return [];
  const stem = `kept-${tab}-${hashOf(`${userId}:${patientId}`)}-`;
  return readOutbox(userId, patientId, now).filter((p) => p.key.startsWith(stem) && p.hold === 'kept' && p.why === 'tab');
}

/**
 * Every outbox note another tab made from THIS tab's box, whatever it is doing now (fix round 8): still
 * kept, being edited there, or sent there and not yet confirmed. Only the first is this tab's to take back.
 */
export function takenCopiesOf(userId: string | null | undefined, patientId: string | null | undefined, tab: string, now = Date.now()): StoredNote[] {
  if (!usable(userId, patientId)) return [];
  const stem = `kept-${tab}-${hashOf(`${userId}:${patientId}`)}-`;
  return readOutbox(userId, patientId, now).filter((p) => p.key.startsWith(stem));
}

/* ── "Sent from another tab" marks (fix round 8) ──────────────────────────────
 * A tab that sends the "Draft kept" copy of ANOTHER tab's box (an adopted key, `kept-<tab>-…`) leaves a
 * short-lived mark: `pa.sent.v1:<hash of user and resident>:<the adopted key>:<day>`, holding only the
 * recorded note's id and the key. No words, no name. When the tab whose box it was wakes and finds its box
 * gone and no copy left, the mark tells it its words were SENT, not lost: it does not write them back (a
 * second Send would record them twice), and it can point at the note in the thread. It lives as long as a
 * note may wait here (7 days, by the day in its name): a tab asleep longer than that has no words left
 * to send either.
 */
export const SENT_PREFIX = 'pa.sent.v1:';
export interface SentMark { key: string; noteId: string; at: number }
const ADOPTED_RE = /^kept-(t[a-z0-9]{6,24})-([a-z0-9]+)-([a-z0-9]+)$/;
/** True for the key of a "Draft kept" copy of another tab's box (made by adoptLeftoverDrafts). */
export const isAdoptedKey = (noteKey: string): boolean => ADOPTED_RE.test(noteKey);
const sentStem = (userId: string, patientId: string, adopted: string) => `${SENT_PREFIX}${hashOf(`${userId}:${patientId}`)}:${slotOf(adopted)}:`;

/** The adopted copy `noteKey` was recorded as note `noteId`: leave the text-free mark for the tab it came from. */
export function leaveSentMark(userId: string | null | undefined, patientId: string | null | undefined, noteKey: string, noteId: string, now = Date.now()): void {
  if (!usable(userId, patientId) || !isAdoptedKey(noteKey) || !noteId) return;
  const s = store();
  if (!s) return;
  const stem = sentStem(userId!, patientId!, noteKey);
  try {
    // Written first, then the older day's mark for the same copy goes (fix round 9: never removed for a write that fails).
    const next = `${stem}${dayOf(now)}`;
    s.setItem(next, JSON.stringify({ key: noteKey, noteId: String(noteId), at: now }));
    keysStartingWith(s, stem).filter((k) => k !== next).forEach((k) => { try { s.removeItem(k); } catch { /* keep going */ } });
  } catch { /* quota or blocked: the waking tab falls back to its caution */ }
}

/**
 * The marks left for exactly THIS tab's box: the one it last saved at `savedAt`. Fix round 9: only that
 * box's mark counts. The old fallback (any mark for this tab) let a mark left for EARLIER words -- words a
 * reload had already dropped from memory, so the mark was never read -- speak for new words whose copy was
 * then discarded: the new words were dropped and called sent. A mark older than the box (sent before these
 * words were saved) never speaks for it either; and with no saved box (0) there is no copy to have been sent.
 */
export function sentMarksFor(userId: string | null | undefined, patientId: string | null | undefined, tab: string, savedAt = 0, now = Date.now()): SentMark[] {
  if (!usable(userId, patientId)) return [];
  const s = store();
  if (!s) return [];
  const stem = `${SENT_PREFIX}${hashOf(`${userId}:${patientId}`)}:kept-${tab}-`;
  const out: SentMark[] = [];
  try {
    for (const k of keysStartingWith(s, stem)) {
      const m = /:(\d{1,7})$/.exec(k);
      if (!m || expiredByName(Number(m[1]), now)) { try { s.removeItem(k); } catch { /* keep going */ } continue; }
      let v: Partial<SentMark> | null = null;
      try { v = JSON.parse(s.getItem(k) || 'null'); } catch { v = null; }
      if (!v || typeof v.key !== 'string' || typeof v.noteId !== 'string' || typeof v.at !== 'number' || now - v.at > NOTE_STORAGE_TTL_MS) continue;
      out.push({ key: v.key, noteId: v.noteId, at: v.at });
    }
  } catch { return []; }
  if (!(savedAt > 0)) return [];
  return out
    .filter((x) => { const m = ADOPTED_RE.exec(x.key); return Boolean(m) && parseInt(m![3], 36) === savedAt && x.at >= savedAt; })
    .sort((a, b) => b.at - a.at);
}

/**
 * Every mark left for this tab's boxes on this resident goes, unread (fix round 9). A thread that has just
 * opened holds no words in memory that a mark could describe -- its box, if any, is read from storage -- so
 * a mark left from before (a reload, the panel closed and opened) can only ever be stale.
 */
export function clearTabSentMarks(userId: string | null | undefined, patientId: string | null | undefined, tab: string): void {
  if (!usable(userId, patientId)) return;
  const s = store();
  if (!s) return;
  try {
    keysStartingWith(s, `${SENT_PREFIX}${hashOf(`${userId}:${patientId}`)}:kept-${tab}-`).forEach((k) => { try { s.removeItem(k); } catch { /* keep going */ } });
  } catch { /* storage unavailable */ }
}

/** The waking tab has read its marks: they go (nothing in them is needed again). */
export function clearSentMarks(userId: string | null | undefined, patientId: string | null | undefined, marks: SentMark[]): void {
  if (!usable(userId, patientId)) return;
  const s = store();
  if (!s) return;
  marks.forEach((x) => {
    try { keysStartingWith(s, sentStem(userId!, patientId!, x.key)).forEach((k) => { try { s.removeItem(k); } catch { /* keep going */ } }); } catch { /* keep going */ }
  });
}

/** True when a storage event is this person's sign-out (config/authRoutes leaves the marker before it clears her keys). */
export function isSignOutKeyFor(storageKey: string | null, userId: string | null | undefined): boolean {
  return Boolean(storageKey && userId && storageKey === signedOutKey(userId));
}

/** The note keys the boxes of OTHER tabs that still have this thread open are editing (left as "Editing"). */
export function liveEditKeys(userId: string | null | undefined, patientId: string | null | undefined, myTab: string, now = Date.now()): Set<string> {
  const out = new Set<string>();
  if (!usable(userId, patientId)) return out;
  for (const d of otherTabDrafts(userId, patientId, myTab, now)) {
    if (d.edit && d.tab && isThreadOpenIn(d.tab, userId!, patientId!, now)) out.add(d.edit.key);
  }
  return out;
}

/** True when a storage event touched THIS tab's own box for this resident (a duplicate of this tab wrote it). */
export function isOwnDraftKey(storageKey: string | null, userId: string | null | undefined, patientId: string | null | undefined, tab: string): boolean {
  if (!storageKey || !usable(userId, patientId)) return false;
  return storageKey.startsWith(draftTabStem(userId!, patientId!, tab));
}

/*
 * ONE KEY PER UNSENT NOTE (fix round 6). The outbox used to be one list under one key, changed by
 * read-modify-write: two tabs writing at once could each write back a stale copy and drop the other's
 * note. Now every note has its own key, `pa.notes.v3:outbox:<userId>:<patientId>:<slot>:<epochDay>`
 * (the slot is the note's idempotency key, or a hash of it when it holds characters a key name may
 * not), so adding, changing or removing one note never rewrites another's. The value is one note; the
 * envelope's savedAt is when the note was first put here and orders the list (a change of hold keeps
 * it). The old one-list key (rounds 3 to 5, never released) is split into per-note keys on read.
 */
const SLOT_RE = /^[A-Za-z0-9_.-]{1,120}$/;
const slotOf = (noteKey: string): string => (SLOT_RE.test(noteKey) ? noteKey : `h~${hashOf(noteKey)}`);
const noteStem = (userId: string, patientId: string, noteKey: string) => `${noteKeyStem('outbox', userId, patientId)}${slotOf(noteKey)}:`;
// Two notes put here in the same millisecond still have an order (the second is a millisecond later).
let lastAdded = 0;
function addedAt(now: number): number {
  const t = now <= lastAdded && lastAdded - now < 1000 ? lastAdded + 1 : now;
  lastAdded = Math.max(lastAdded, t);
  return t;
}

function asStoredNote(p: unknown): StoredNote | null {
  const v = p as Partial<StoredNote> | null;
  if (!v || typeof v !== 'object' || typeof v.key !== 'string' || !v.key || typeof v.note !== 'string' || typeof v.caseId !== 'string') return null;
  const out: StoredNote = { key: v.key, caseId: v.caseId, kind: v.kind === 'VETTING' ? 'VETTING' : 'GENERAL', note: v.note };
  if (v.hold === 'kept' || v.hold === 'editing') out.hold = v.hold;
  if (out.hold === 'editing' && v.from === 'kept') out.from = 'kept';
  if (out.hold && (v.why === 'tab' || v.why === 'earlier')) out.why = v.why;
  if (typeof v.caution === 'number' && Number.isFinite(v.caution) && v.caution > 0) {
    out.caution = v.caution;
    if (v.partial === true) out.partial = true;
  }
  return out;
}
/** Only what a note is: its key, case, kind and words, why it waits, and its caution (a time, and whether partial). Nothing else is ever stored. */
const minimal = ({ key, caseId, kind, note, hold, from, why, caution, partial }: StoredNote) => ({
  key, caseId, kind, note, ...(hold ? { hold } : {}), ...(hold === 'editing' && from ? { from } : {}), ...(hold && why ? { why } : {}),
  ...(caution && caution > 0 ? { caution, ...(partial ? { partial: true } : {}) } : {}),
});

/** The old one-list key (`...outbox:<user>:<patient>:<day>`): each of its notes gets its own key, then it goes. */
function splitOldList(s: Storage, userId: string, patientId: string, now: number): void {
  const stem = noteKeyStem('outbox', userId, patientId);
  for (const k of keysStartingWith(s, stem)) {
    if (!/^\d{1,7}$/.test(k.slice(stem.length))) continue;
    let v: { savedAt?: unknown; value?: unknown } | null = null;
    try { v = JSON.parse(s.getItem(k) || 'null'); } catch { v = null; }
    const ok = v && typeof v === 'object' && fresh(v.savedAt, now);
    if (ok && Array.isArray(v!.value)) {
      (v!.value as unknown[]).forEach((p, i) => {
        const note = asStoredNote(p);
        // A note already under its own key (another tab split the list first) is left as it is.
        if (note && readEntry(noteStem(userId, patientId, note.key), now) === null) writeOwn(noteStem(userId, patientId, note.key), minimal(note), (v!.savedAt as number) + i);
      });
    }
    try { s.removeItem(k); } catch { /* keep going */ }
  }
}

/** One stored value with the time its words were saved and its list position (expired or malformed: dropped, null). */
function readEntry(stem: string, now: number): { value: unknown; savedAt: number; order: number } | null {
  const s = store();
  if (!s) return null;
  try {
    const ks = keysStartingWith(s, stem).sort((a, b) => (keyDay(b) || 0) - (keyDay(a) || 0));
    let found: { value: unknown; savedAt: number; order: number } | null = null;
    let foundKey = '';
    let foundRaw: string | null = null;
    for (const k of ks) {
      let v: { savedAt?: unknown; order?: unknown; value?: unknown } | null = null;
      const raw = s.getItem(k);
      try { v = JSON.parse(raw || 'null'); } catch { v = null; }
      const ok = v && typeof v === 'object' && fresh(v.savedAt, now);
      if (ok && found === null) {
        const savedAt = v!.savedAt as number;
        found = { value: v!.value, savedAt, order: typeof v!.order === 'number' && Number.isFinite(v!.order) ? v!.order as number : savedAt };
        foundKey = k;
        foundRaw = raw;
        continue;
      }
      try { s.removeItem(k); } catch { /* keep going */ }
    }
    // Fix round 11: saved "in the future" (the clock was moved back since): saved again, dated NOW, under
    // today's name, so it ages from now and expires seven days on -- it used to count as now forever. Its
    // place in the list is kept (no later than now). A write that does not land is tried in place, under the
    // same name (the prune renames that); and if that fails too, the value is still read as saved now.
    // Fix round 12: re-read just before the write, and written only if storage still holds exactly what was
    // read (one key, the same bytes): another tab that wrote this value meanwhile (a keystroke, a Send) is
    // never overwritten with the older words read here. Unchanged or not, it is read as saved now.
    if (found && found.savedAt > now) {
      const order = Math.min(found.order, now);
      const unchanged = (): boolean => {
        try { return s.getItem(foundKey) === foundRaw && keysStartingWith(s, stem).every((k) => k === foundKey); } catch { return false; }
      };
      if (unchanged() && !writeOwn(stem, found.value, now, order) && unchanged()) {
        try { s.setItem(foundKey, JSON.stringify(order !== now ? { savedAt: now, order, value: found.value } : { savedAt: now, value: found.value })); } catch { /* read as now */ }
      }
      found = { value: found.value, savedAt: now, order };
    }
    return found;
  } catch {
    return null;
  }
}

/** Every unsent note this user has for this resident, oldest first. */
export function readOutbox(userId: string | null | undefined, patientId: string | null | undefined, now = Date.now()): StoredNote[] {
  if (!usable(userId, patientId)) return [];
  const s = store();
  if (!s) return [];
  try {
    splitOldList(s, userId!, patientId!, now);
    const stem = noteKeyStem('outbox', userId!, patientId!);
    const slots = new Set<string>();
    for (const k of keysStartingWith(s, stem)) {
      const m = /^([^:]{1,130}):\d{1,7}$/.exec(k.slice(stem.length));
      if (m) slots.add(m[1]);
    }
    const out: Array<{ note: StoredNote; at: number }> = [];
    slots.forEach((slot) => {
      const e = readEntry(`${stem}${slot}:`, now);
      const note = e ? asStoredNote(e.value) : null;
      if (note && slotOf(note.key) === slot) out.push({ note, at: e!.order });
    });
    return out.sort((a, b) => a.at - b.at || (a.note.key < b.note.key ? -1 : 1)).map((x) => x.note);
  } catch {
    return [];
  }
}

/** One unsent note, read from storage now (null: not there -- sent, confirmed or discarded elsewhere). */
export function readOutboxNote(userId: string | null | undefined, patientId: string | null | undefined, noteKey: string, now = Date.now()): StoredNote | null {
  if (!usable(userId, patientId)) return null;
  const e = readEntry(noteStem(userId!, patientId!, noteKey), now);
  const note = e ? asStoredNote(e.value) : null;
  return note && note.key === noteKey ? note : null;
}

/** Replace the whole outbox. Tests and migrations only: the thread adds, changes and removes one note at a time. */
export function writeOutbox(userId: string | null | undefined, patientId: string | null | undefined, list: StoredNote[], now = Date.now()): void {
  if (!usable(userId, patientId)) return;
  const s = store();
  if (!s) return;
  try { keysStartingWith(s, noteKeyStem('outbox', userId!, patientId!)).forEach((k) => { try { s.removeItem(k); } catch { /* keep going */ } }); } catch { /* storage unavailable */ }
  list.forEach((p) => { const note = asStoredNote(p); if (note) writeOwn(noteStem(userId!, patientId!, note.key), minimal(note), addedAt(now)); });
}

/**
 * Put one note under its own key. A note already there (an edited note being sent with its own key)
 * keeps its place in the list. No other note's key is read or written.
 *
 * Fix round 7: the 7-day limit counts from when the WORDS were written. A note whose words, kind or case
 * change here (an old unsent note edited and sent again) is saved now, with a fresh expiry; only its
 * place in the list (`order`) is kept. Unchanged words keep their own time.
 */
export function addToOutbox(userId: string | null | undefined, patientId: string | null | undefined, note: StoredNote, now = Date.now()): void {
  if (!usable(userId, patientId)) return;
  const clean = asStoredNote(note);
  if (!clean) return;
  const stem = noteStem(userId!, patientId!, clean.key);
  const was = readEntry(stem, now);
  const prior = was ? asStoredNote(was.value) : null;
  const sameWords = Boolean(prior && prior.note === clean.note && prior.kind === clean.kind && prior.caseId === clean.caseId);
  if (!was) { writeOwn(stem, minimal(clean), addedAt(now)); return; }
  writeOwn(stem, minimal(clean), sameWords ? was.savedAt : now, was.order);
}

/** Change why one note waits (kept / editing / unsent), in place. A note that is not there stays not there. */
export function setOutboxHold(userId: string | null | undefined, patientId: string | null | undefined, noteKey: string, hold: StoredHold | null, from?: 'kept' | null, now = Date.now()): void {
  if (!usable(userId, patientId)) return;
  const stem = noteStem(userId!, patientId!, noteKey);
  const was = readEntry(stem, now);
  const p = was ? asStoredNote(was.value) : null;
  if (!p || p.key !== noteKey) return;
  const next: StoredNote = { key: p.key, caseId: p.caseId, kind: p.kind, note: p.note };
  if (hold) next.hold = hold;
  if (hold === 'editing' && from) next.from = from;
  if (hold && p.why) next.why = p.why;
  // Fix round 10: the caution stays with the words whatever they wait as (kept, editing, sent and not confirmed).
  if (p.caution) next.caution = p.caution;
  if (p.caution && p.partial) next.partial = true;
  // Its words are unchanged: their own time and their place in the list both stay.
  writeOwn(stem, minimal(next), was!.savedAt, was!.order);
}

/**
 * Put a caution on one unsent note, or take it off (null), in place (fix round 10). A caution already there
 * keeps the EARLIER time (the check then pages back further). A note that is not there stays not there.
 * `partial` (fix round 11) is added to the caution, never taken from it but with the caution itself.
 */
export function setOutboxCaution(
  userId: string | null | undefined, patientId: string | null | undefined, noteKey: string, caution: number | null, now = Date.now(), partial = false,
): void {
  if (!usable(userId, patientId)) return;
  const stem = noteStem(userId!, patientId!, noteKey);
  const was = readEntry(stem, now);
  const p = was ? asStoredNote(was.value) : null;
  if (!p || p.key !== noteKey) return;
  const next: StoredNote = { ...p };
  if (caution && caution > 0) {
    next.caution = p.caution ? Math.min(p.caution, caution) : caution;
    if (partial || (p.caution && p.partial)) next.partial = true;
  } else { delete next.caution; delete next.partial; }
  if (next.caution === p.caution && Boolean(next.partial) === Boolean(p.partial)) return;
  writeOwn(stem, minimal(next), was!.savedAt, was!.order);
}

/** Remove one note by its key: the server confirmed it, the PA discarded it, or its edited words were sent in its place. */
export function removeFromOutbox(userId: string | null | undefined, patientId: string | null | undefined, noteKey: string): void {
  if (!usable(userId, patientId)) return;
  const s = store();
  if (!s) return;
  try { keysStartingWith(s, noteStem(userId!, patientId!, noteKey)).forEach((k) => { try { s.removeItem(k); } catch { /* keep going */ } }); } catch { /* storage unavailable */ }
}

/** The server confirmed this note: nothing of it stays in the browser, whether or not the thread is still open. */
export const dropConfirmed = removeFromOutbox;

/** True when a storage event (another tab) touched this user's outbox for this resident. */
export function isOwnOutboxKey(storageKey: string | null, userId: string | null | undefined, patientId: string | null | undefined): boolean {
  if (storageKey === null) return true; // storage.clear()
  if (!usable(userId, patientId)) return false;
  return storageKey.startsWith(noteKeyStem('outbox', userId, patientId!));
}

/* ── Words on this page only (fix round 12) ─────────────────────────────────── */

/**
 * Words that could not be written to storage (a full quota, blocked site data) live on screen only. The thread
 * used to hold them in its own state, so they were lost the moment it unmounted inside the app: another section
 * of the panel, Prev/Next, the panel closed. They are now also held here, in this page's memory -- never in
 * storage -- under the user, the resident and the tab, and the thread reads them back when it opens again.
 * The thread keeps this in step with what is on screen only: once its words are stored (or sent, or
 * discarded) they leave. A sign-out drops them: the thread's own sign-out handling, a sign-out marker newer
 * than them (read here), and the workspace leaving without her session (forgetMemoryWithoutSession).
 * While any are held, leaving or reloading the page asks first, even with the thread not on screen.
 */
/**
 * Fix round 13: the memory copy only ever ADDS words storage never held. A box is held only while the screen
 * differs from what storage holds for it, and it carries what storage held (`stored`, saved at `storedAt`), so a
 * reopened thread knows the box storage had -- which another tab may have taken and sent while the thread was
 * closed -- and runs the same taken-box check a waking tab runs (sent: a partial caution; kept: both kept).
 */
export interface HeldBox { text: string; edit: DraftEdit | null; caseId?: string; kind?: StoredKind; caution?: number; partial?: boolean; stored?: string; storedAt?: number }
export interface HeldInMemory { box: HeldBox | null; notes: StoredNote[]; at: number }
const memory = new Map<string, HeldInMemory & { userId: string }>();
const memKey = (userId: string, patientId: string, tab: string) => JSON.stringify([userId, patientId, tab]);
const MEMORY_LEAVE_WARNING = 'You have words that aren’t saved on this computer. Leave anyway?';
const warnOnLeave = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = MEMORY_LEAVE_WARNING; return MEMORY_LEAVE_WARNING; };
let warning = false;
function syncLeaveWarning(): void {
  const want = memory.size > 0;
  if (want === warning) return;
  warning = want;
  try {
    if (want) window.addEventListener('beforeunload', warnOnLeave);
    else window.removeEventListener('beforeunload', warnOnLeave);
  } catch { /* no window */ }
}
/** Hold (or, with nothing left, let go of) what this thread has on screen only. */
export function holdInMemory(
  userId: string | null | undefined, patientId: string | null | undefined, tab: string, box: HeldBox | null, notes: StoredNote[], now = Date.now(),
): void {
  if (!usable(userId, patientId)) return;
  const k = memKey(userId, patientId!, tab);
  const keepBox = box && box.text.trim() ? { ...box } : null;
  const keepNotes = notes.map((n) => asStoredNote(n)).filter((n): n is StoredNote => n !== null);
  if (!keepBox && !keepNotes.length) memory.delete(k);
  else memory.set(k, { userId, box: keepBox, notes: keepNotes, at: now });
  syncLeaveWarning();
}
/** What this page holds for her on this resident, from this tab (null: nothing; dropped once she signed out since). */
export function heldInMemory(userId: string | null | undefined, patientId: string | null | undefined, tab: string, now = Date.now()): HeldInMemory | null {
  if (!usable(userId, patientId)) return null;
  const k = memKey(userId, patientId!, tab);
  const h = memory.get(k);
  if (!h) return null;
  const out = signedOutAt(userId, now);
  if ((out !== null && out >= h.at) || ageOf(h.at, now) > NOTE_STORAGE_TTL_MS) { memory.delete(k); syncLeaveWarning(); return null; }
  return { box: h.box, notes: h.notes, at: h.at };
}
/** One note held here went (the server confirmed it, even after the thread closed). */
export function forgetMemoryNote(userId: string | null | undefined, patientId: string | null | undefined, noteKey: string): void {
  if (!usable(userId, patientId)) return;
  memory.forEach((h, k) => {
    const [u, p] = JSON.parse(k) as [string, string, string];
    if (u !== userId || p !== patientId || !h.notes.some((n) => n.key === noteKey)) return;
    const notes = h.notes.filter((n) => n.key !== noteKey);
    if (!h.box && !notes.length) memory.delete(k); else memory.set(k, { ...h, notes });
  });
  syncLeaveWarning();
}
/**
 * Fix round 13: a note's send confirmed -- possibly by a thread that has since closed, while a reopened thread
 * shows the same note (same key) as "Not sent". The open thread hears it here and lets the note go.
 */
/**
 * Fix round 14: `replacement` -- the send was answered "already recorded" with OTHER words, so the note under
 * `noteKey` is the server's and these words now wait as a kept draft under a new key (the open thread swaps them in).
 */
type ConfirmedListener = (userId: string, patientId: string, noteKey: string, replacement: StoredNote | null) => void;
const confirmedListeners = new Set<ConfirmedListener>();
export function onNoteConfirmed(fn: ConfirmedListener): () => void {
  confirmedListeners.add(fn);
  return () => { confirmedListeners.delete(fn); };
}
export function announceConfirmed(userId: string | null | undefined, patientId: string | null | undefined, noteKey: string, replacement: StoredNote | null = null): void {
  if (!usable(userId, patientId)) return;
  confirmedListeners.forEach((fn) => { try { fn(userId, patientId!, noteKey, replacement); } catch { /* one listener never stops the rest */ } });
}
/**
 * Fix round 14: the note under `oldKey` went to the server (an earlier version of it), and `note` -- the words that
 * did NOT -- could not be stored. They replace it in this page's memory, under this tab, so a send that settles
 * after the thread closed never leaves them nowhere: memory, warned on leaving, back on reopen as a kept draft.
 */
export function replaceMemoryNote(
  userId: string | null | undefined, patientId: string | null | undefined, tab: string, oldKey: string, note: StoredNote, now = Date.now(),
): void {
  if (!usable(userId, patientId)) return;
  const clean = asStoredNote(note);
  if (!clean) return;
  forgetMemoryNote(userId, patientId, oldKey);
  const k = memKey(userId, patientId!, tab);
  const h = memory.get(k);
  const notes = [...(h ? h.notes.filter((n) => n.key !== clean.key) : []), clean];
  memory.set(k, { userId, box: h ? h.box : null, notes, at: now });
  syncLeaveWarning();
}
/** She signed out: nothing of hers stays in this page's memory. */
export function forgetInMemory(userId: string | null | undefined): void {
  if (!userId) return;
  memory.forEach((h, k) => { if (h.userId === userId) memory.delete(k); });
  syncLeaveWarning();
}
/**
 * The workspace is leaving: whoever has no session here any more (signed out, expired) keeps nothing in memory.
 * Fix round 14: only when that can be SHOWN -- no session token at all, or one issued to someone else. A token whose
 * user cannot be read is not taken for her absence: her words stay, held under the user the thread was opened with.
 */
export function forgetMemoryWithoutSession(): void {
  const sid = sessionUserId();
  memory.forEach((h, k) => { if (sid === null || (sid !== '' && sid !== String(h.userId))) memory.delete(k); });
  syncLeaveWarning();
}
/** Tests: start from nothing held. */
export function forgetAllInMemory(): void {
  memory.clear();
  syncLeaveWarning();
}
